import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { apiClient } from "../App";

const PermissionsContext = createContext(null);

export function PermissionsProvider({ children, user }) {
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const fetchPermissions = useCallback(async () => {
    if (!user) {
      setPermissions(null);
      setLoading(false);
      return;
    }
    
    try {
      const res = await apiClient.get("/my-permissions");
      setPermissions(res.data.permissions);
    } catch (err) {
      console.error("Failed to fetch permissions", err);
      // Fall back to role-based defaults
      const role = user?.role || "Artist";
      const defaults = {
        "COO": { can_view_financials: true, can_view_costing: true, can_view_all_programs: true },
        "Admin": { can_view_financials: true, can_view_costing: true, can_view_all_programs: true },
        "Program Manager": { can_view_financials: false, can_view_costing: false, can_view_all_programs: false },
        "Artist": { can_view_financials: false, can_view_costing: false, can_view_all_programs: false },
      };
      setPermissions(defaults[role] || defaults["Artist"]);
    } finally {
      setLoading(false);
    }
  }, [user]);
  
  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);
  
  const value = {
    permissions,
    loading,
    refetch: fetchPermissions,
    // Convenience methods
    canViewFinancials: permissions?.can_view_financials ?? false,
    canViewCosting: permissions?.can_view_costing ?? false,
    canViewAllPrograms: permissions?.can_view_all_programs ?? false,
    canManageReceivables: permissions?.can_manage_receivables ?? false,
    canViewAgreements: permissions?.can_view_agreements ?? (user?.role === "COO" || user?.role === "Admin"),
    isFinancialRestricted: permissions?._financial_restricted ?? false,
  };
  
  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    // Return safe defaults if used outside provider
    return {
      permissions: null,
      loading: true,
      canViewFinancials: false,
      canViewCosting: false,
      canViewAllPrograms: false,
      canManageReceivables: false,
      canViewAgreements: false,
      isFinancialRestricted: false,
    };
  }
  return context;
}
